import { ApplicationConfig, importProvidersFrom, inject } from '@angular/core';
import { ApiModule } from './api/api.module';
import { ApiConfigurations, AppWebSockets } from './constants';
import { WebsocketModule } from './websocket';
import { ImportModule } from './import';
import { provideSettingsInit, SettingsService } from './settings';
import { LoggingService } from './logging';
import {
  createDesktopViewTransitionsFilter,
  provideAppInformation,
  provideNavigation,
  useIcsRoutingTitles,
} from 'ics-core';
import { environment } from '../environments/environment';
import { APP_NAVIGATION } from './app.navigation';
import { IcsCdkStorageService, useIcsStorage } from '@ics-cdk/core/storage';
import { withUserIdAsStorageKey } from '@ics-cdk/core/interop';
import {
  loadAuthorization,
  useIcsAuthorization,
  withAuthorizations,
  withAuthorizationType,
  withRoles,
} from '@ics-cdk/core/authorization';
import { ALL_ROLES, PrivilegeLevels, RoleService } from './roles';
import {
  LogLevel,
  useIcsLogging,
  withConsoleLogger,
  withLogLevel,
} from '@ics-cdk/logger';
import { useIcsMaterial3Theming } from 'ics-theme/material-3';
import { useIcsLanguageSupport } from '@ics-cdk/core/i18n';
import { I18N_MODULES } from './i18n-modules';
import { addIcsWebAuthentication } from 'ics-identity-authentication';
import {
  addIcsMessenger,
  enableMessengerNavigation,
  enableMessengerRouteTitles,
} from '@ics-angular/messenger';
import { withCdkAuthenticationForMessenger } from '@ics-cdk/messenger/interop';
import {
  useExperimentalConnectionLock,
  withHubConnection,
} from '@ics-cdk/messenger';
import { provideServiceWorker } from '@angular/service-worker';
import {
  provideRouter,
  withComponentInputBinding,
  withHashLocation,
  withViewTransitions,
} from '@angular/router';
import { appRoutes } from './app.routes';
import {
  STATE_DEBOUNCE_TIME,
  STATE_OUTPUT_DELAY,
} from './entity-state/model/state-storage';
import {
  provideAppUpdateTracking,
  provideHistoryTracking,
  provideIconRegistry,
} from './core';

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      ApiModule.forApis(ApiConfigurations),
      WebsocketModule.forRoot(AppWebSockets),
      ImportModule.forRoot({
        columns: SettingsService,
        logging: LoggingService,
      })
    ),

    // CORE
    provideIconRegistry(),
    provideHistoryTracking(),
    provideAppUpdateTracking(),
    provideSettingsInit(),

    // STATE MANAGEMENT
    {
      provide: STATE_OUTPUT_DELAY,
      useValue: 400, // this delay is used to debounce state changes and secure the DOM from crashing from to many data
    },
    {
      provide: STATE_DEBOUNCE_TIME,
      useValue: 300,
    },

    // ICS PROVIDERS
    provideAppInformation({
      version: environment.version,
      logos: [
        {
          source: '/assets/ics-tls-logo.svg',
          alt: 'ICS TLS Logo',
        },
        {
          source: '/assets/logos/pck-logo.png',
          alt: 'PCK Logo',
        },
      ],
    }),
    // using this to merge this with the messenger navigation
    provideNavigation(APP_NAVIGATION),
    useIcsRoutingTitles(),
    useIcsStorage('ICS-TLS', withUserIdAsStorageKey()),
    useIcsAuthorization(
      loadAuthorization(() => inject(RoleService).getUserRoles()),
      withAuthorizations(ALL_ROLES),
      withRoles(PrivilegeLevels),
      withAuthorizationType('single')
    ),
    useIcsLogging(withLogLevel(LogLevel.Error), withConsoleLogger()),
    useIcsMaterial3Theming(),
    useIcsLanguageSupport(IcsCdkStorageService, I18N_MODULES),
    addIcsWebAuthentication({
      authority: 'identity',
      settings: environment.authentication,
    }),
    addIcsMessenger(
      withCdkAuthenticationForMessenger(),
      withHubConnection(environment.messenger),
      enableMessengerRouteTitles(),
      enableMessengerNavigation(),
      useExperimentalConnectionLock()
    ),

    // END ICS PROVIDERS

    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideRouter(
      appRoutes,
      withHashLocation(),
      withComponentInputBinding(),
      withViewTransitions({
        onViewTransitionCreated: createDesktopViewTransitionsFilter(),
      })
    ),
  ],
};
